<template>
  <div class="store">
    <div class="container">
      <div class="store__wrapper">
        <Transition name="fromTop" mode="out-in">
          <div class="store__block" :class="[{ active: isSearchPage}]" v-if="isSearchPage">
            <div class="store__block-wrapper">
              <input
                  type="text"
                  class="store__block-input"
                  v-model="searchInput"
              >
              <svg viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.826 13.5431L11.8831 10.6003C12.7329 9.4896 13.2389 8.10186 13.2389 6.59887C13.2389 2.9602 10.2787 0 6.6403 0C4.87756 0 3.2202 0.686765 1.97412 1.93281C0.728043 3.17925 0.0412598 4.83617 0.0412598 6.59887C0.0412598 10.2371 3.00153 13.1973 6.6403 13.1973C8.14333 13.1973 9.53111 12.6917 10.6418 11.8416L13.5847 14.7848C13.8717 15.0717 14.3366 15.0717 14.6236 14.7848L14.826 14.5815C15.113 14.2946 15.113 13.8296 14.826 13.5427V13.5431ZM6.63951 11.4662C3.95553 11.4662 1.77203 9.28278 1.77203 6.59887C1.77203 5.29875 2.27844 4.07639 3.1981 3.15675C4.11775 2.23712 5.34015 1.73152 6.6403 1.73152C9.32428 1.73152 11.5074 3.91496 11.5074 6.59887C11.5074 9.28278 9.32389 11.4662 6.6403 11.4662H6.63951Z"
                    fill="currentColor" fill-opacity="0.75"/>
              </svg>
            </div>
          </div>
        </Transition>
        <div class="store__head">
          <p class="store__head-category"><span v-if="category">{{ category ? category : '' }}</span></p>
          <div class="store__head-buttons">
            <div class="store__head-button" @click="setNameOrdering">
              {{ t('buttons.sortName') }}
              <span class="store__head-button-inner">
                <span
                    :class="orderBy === 'name' ? 'store__head-button-small' : 'store__head-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'name' ? 'store__head-button-active' : 'store__head-button-small'"
                />
              </span>
            </div>
            <div class="store__head-button" @click="setPriceOrdering">
              {{ t('buttons.sortPrice') }}
              <span class="store__head-button-inner">
                <span
                    :class="orderBy === 'price' ? 'store__head-button-small' : 'store__head-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'price' ? 'store__head-button-active' : 'store__head-button-small'"
                />
              </span>
            </div>
          </div>
        </div>
        <div class="store__list" v-if="products">
          <product-card
              v-for="product in products"
              :key="product.node.uuid"
              :product="product.node"
          />
          <div class="store__list-observer" ref="observer"></div>
        </div>
        <div class="store__list" v-if="isLoading">
          <product-card-skeleton v-for="idx in 12" :key="idx" />
        </div>
      </div>
    </div>
    <Transition name="opacity" mode="out-in">
      <product-details v-if="route.query.productUuid" />
    </Transition>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {useRoute} from "vue-router";
import {computed, onMounted, ref, watch} from "vue";
import {useIntersectionObserver} from "@vueuse/core";
import {debounce} from "@/core/helpers/debounce.js";
import {Products} from "@/core/api/products.js";
import ProductCard from "@/components/product/product-card.vue";
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import ProductDetails from "@/components/product/product-details.vue";

const {t} = useI18n()
const route = useRoute()

const products = ref([])
const pageInfo = ref([])
const cursorForAfter = ref('0')
const searchInput = ref(route.query['name'] ? (route.query['name']) : '')
const orderBy = ref(route.query['orderBy'] ? String(route.query['orderBy']) : '')
const name = ref(route.query['name'] ? (route.query['name']) : '')

const toggleOrdering = (type) => {
  const currentType = orderBy.value.startsWith('-') ? orderBy.value.slice(1) : orderBy.value;
  orderBy.value = (currentType === type) ? (orderBy.value.startsWith('-') ? type : `-${type}`) : type;
};
const setPriceOrdering = () => toggleOrdering('price');
const setNameOrdering = () => toggleOrdering('name');

const category = computed(() => {
  return route.query.category
})
const isSearchPage = computed(() => {
  return route.name === 'search'
})

const isLoading = ref(false)
const observer = ref(null)

const { stop } = useIntersectionObserver(
  observer,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting && pageInfo.value.hasNextPage) {
      getMoreProducts()
    }
  },
)

const query = computed(() => {
  let res =`?orderBy=${orderBy.value}`

  if (category.value) {
    res = res + `&categories=${category.value}`
  }

  if (name.value) {
    res = res + `&name=${name.value}`
  }

  return res
})

const getProducts = async () => {
  isLoading.value = true
  cursorForAfter.value = '0'

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 12` +
      `, orderBy: "${orderBy.value}"` +
      (category.value ? `, categories: "${category.value}"` : "") +
      (name.value ? `, name: "${name.value}"` : "")
  )
  products.value = res.data?.products?.edges
  pageInfo.value = res.data?.products?.pageInfo

  isLoading.value = false
}

const getMoreProducts = async () => {
  isLoading.value = true

  cursorForAfter.value = pageInfo.value.endCursor

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 12` +
      (category.value ? `, categories: "${category.value}"` : "") +
      `, orderBy: "${orderBy.value}"` +
      (name.value ? `, name: "${name.value}"` : "")
  )
  products.value.push(...res.data.products.edges)
  pageInfo.value = res.data.products.pageInfo

  isLoading.value = false
}

const updateNameDebounced = debounce(() => {
  name.value = searchInput.value;
}, 500);
watch(
  () => searchInput.value,
  () => {
    updateNameDebounced();
  }
)

watch(isSearchPage, (newVal) => {
  const mainElement = document.querySelector('.main');
  if (mainElement) {
    mainElement.style.paddingTop = newVal ? '150px' : '92px';
  }
});

watch(
  () => query.value,
  async () => {
    products.value = [];
    history.pushState({}, '', route.path + query.value);
    await getProducts();
  }
)

onMounted( async () => {
  await getProducts()

  const mainElement = document.querySelector('.main');
  if (isSearchPage.value) {
    mainElement.style.paddingTop = '150px';
  } else {
    mainElement.style.paddingTop = '92px';
  }
})
</script>

<style lang="scss" scoped>
.store {
  padding-block: 50px 100px;
  background-image: url(@images/bg/storeBg.png);

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 50px;
  }

  &__block {
    width: 100%;
    height: 55px;
    position: absolute;
    top: 35px;
    display: grid;
    place-items: center;
    background-color: $main;
    border-top: 2px solid $yellow;
    transition: 0.2s;

    &.active {
      top: 92px;
    }

    &-wrapper {
      width: 65%;
      height: 90%;
      color: $main;
      position: relative;
    }

    & svg {
      width: 25px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 15px;
      transition: left 0.2s ease;
    }

    &-input {
      width: 100%;
      height: 100%;
      border-radius: 10px;
      border: 2px solid $main;
      padding-left: 50px;
      transition: 0.2s;

      font-size: 26px;
      font-weight: 500;
      color: $main;
    }
  }

  &__head {
    width: 100%;
    background-color: $main;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 10px;
    padding: 15px 30px;

    &-category {
      font-family: Poppins, sans-serif;
      font-weight: 600;
      font-size: 28px;
      color: $white;
    }

    &-buttons {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    &-button {
      cursor: pointer;
      border-radius: 10px;
      background-color: #628776;
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 10px 15px;

      font-size: 18px;
      font-weight: 500;
      color: $white;

      @include hover {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
      }

      &-inner {
        display: flex;
        align-items: start;
        flex-direction: column;
        justify-content: space-between;
        width: 40px;
        height: 25px;

        & span {
          height: 4px;
          background-color: $white;
          border-radius: 0.5rem;
          width: 75%;
          transition: all 0.3s ease;
        }
      }

      &-small {
        width: 50% !important;
      }

      &-active {
        width: 100% !important;
      }
    }
  }

  &__list {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 362px);
    justify-content: space-between;
    column-gap: 50px;
    row-gap: 37px;

    &-observer {
      width: 100%;
      height: 10px;
      background-color: transparent;
    }
  }
}

@media (max-width: 1680px) {
  .store {
    &__list {
      grid-template-columns: repeat(3, 362px);
    }
  }
}

@media (max-width: 1300px) {
  .store {
    &__list {
      grid-template-columns: repeat(3, 300px);
    }
  }
}

@media (max-width: 1100px) {
  .store {
    &__head {
      &-buttons {
        flex-direction: column;
      }

      &-button {
        width: 100%;
        justify-content: space-between;
      }
    }

    &__list {
      grid-template-columns: repeat(2, 300px);
      justify-content: center;
    }
  }
}

@media (max-width: 650px) {
  .store {
    &__head {
      flex-direction: column;

      &-category {
        & span {
          margin-bottom: 40px;
        }
      }
    }

    &__list {
      grid-template-columns: repeat(1, 300px);
    }
  }
}

@media (max-width: 450px) {
  .store {
    &__block {
      &-wrapper {
        width: 90%;
      }
    }
  }
}
</style>